import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import CampoSenha from '../components/CampoSenha';
import { limparCacheSenhaPadrao } from '../lib/senhaPadrao';
import { api } from '../lib/api';
import { obterConfig, recarregarConfig } from '../lib/configSistema';
import { useToast } from '../context/ToastContext';

const COR_PRIMARIA_PADRAO = '#051c3e';
const COR_DESTAQUE_PADRAO = '#ffc107';

// Configurações do sistema — só superadmin (menu e API). Mapa do cadastro de imóveis e, desde
// 28/09/2026, a Aparência (cor principal, cor de destaque e logotipo do PAINEL). Escopo da
// Aparência: só o painel (login, menu, botões) — os e-mails (api) e as páginas do wl têm HTML
// próprio e não seguem esse tema automaticamente (ver CLAUDE.md).
export default function Configuracoes() {
  const toast = useToast();
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [tipo, setTipo] = useState('openstreetmap');
  const [chave, setChave] = useState('');
  const [corPrimaria, setCorPrimaria] = useState(COR_PRIMARIA_PADRAO);
  const [corDestaque, setCorDestaque] = useState(COR_DESTAQUE_PADRAO);
  const [logoUrl, setLogoUrl] = useState('');
  const [enviandoLogo, setEnviandoLogo] = useState(false);
  const [numeroBot, setNumeroBot] = useState('');
  // só regrava o que foi lido com sucesso (senão um GET que falhou sobrescreveria o valor salvo com o padrão)
  const [botLido, setBotLido] = useState({ numero: false, timeout: false });
  const [timeoutCadastro, setTimeoutCadastro] = useState('30');
  // Regras do cadastro de imóveis (superadmin): prazo para trocar o tipo e busca de endereço dos bots. Só grava se leu.
  const [regras, setRegras] = useState(null);
  const [regrasLidas, setRegrasLidas] = useState(false);
  // Acesso e limites (superadmin): senhas, vínculo com os bots e tamanho de arquivos.
  const [acesso, setAcesso] = useState(null);
  const [acessoLido, setAcessoLido] = useState(false);
  const inputLogoRef = useRef(null);

  useEffect(() => {
    recarregarConfig().then((c) => {
      setTipo(c.mapa.tipo);
      setChave(c.mapa.google_key || '');
      setCorPrimaria(c.aparencia?.corPrimaria || COR_PRIMARIA_PADRAO);
      setCorDestaque(c.aparencia?.corDestaque || COR_DESTAQUE_PADRAO);
      setLogoUrl(c.aparencia?.logoUrl || '');
      setCarregando(false);
    });
    // leitura restrita (só superadmin): o número do bot não vem na lista pública de configurações
    api.get('/configuracoes/bot_cadastro').then((r) => { setTimeoutCadastro(String(r.data?.timeoutMinutos ?? 30)); setBotLido((b) => ({ ...b, timeout: true })); }).catch(() => {});
    api.get('/configuracoes/acesso_regras').then((r) => { setAcesso(r.data); setAcessoLido(true); }).catch(() => {});
    api.get('/configuracoes/imoveis_regras').then((r) => { setRegras(r.data); setRegrasLidas(true); }).catch(() => {});
    api.get('/configuracoes/whatsapp_bot').then((r) => { setNumeroBot(r.data?.numero || ''); setBotLido((b) => ({ ...b, numero: true })); }).catch(() => {});
  }, []);

  // Aplica na hora, sem precisar recarregar a página — a mesma variável CSS que `AparenciaAplicada`
  // define globalmente (assim o superadmin já vê o resultado antes mesmo de clicar em Salvar).
  function previsualizarCor(nome, valor) {
    document.documentElement.style.setProperty(nome, valor);
  }

  async function salvar(e) {
    e.preventDefault();
    setSalvando(true);
    try {
      await Promise.all([
        api.put('/configuracoes/mapa', { tipo, google_key: chave.trim() }),
        api.put('/configuracoes/aparencia', { corPrimaria, corDestaque, logoUrl }),
        ...(botLido.numero ? [api.put('/configuracoes/whatsapp_bot', { numero: numeroBot })] : []),
        ...(regrasLidas && regras ? [api.put('/configuracoes/imoveis_regras', regras)] : []),
        ...(acessoLido && acesso ? [api.put('/configuracoes/acesso_regras', acesso)] : []),
        ...(botLido.timeout ? [api.put('/configuracoes/bot_cadastro', { timeoutMinutos: Number(timeoutCadastro) })] : []),
      ]);
      await recarregarConfig();
      limparCacheSenhaPadrao();
      toast.sucesso('Configurações salvas.');
    } catch (err) {
      toast.erro(err.message || 'Não foi possível salvar');
    } finally {
      setSalvando(false);
    }
  }

  function restaurarCores() {
    setCorPrimaria(COR_PRIMARIA_PADRAO);
    setCorDestaque(COR_DESTAQUE_PADRAO);
    previsualizarCor('--cor-primaria', COR_PRIMARIA_PADRAO);
    previsualizarCor('--cor-destaque', COR_DESTAQUE_PADRAO);
  }

  async function enviarLogo(e) {
    const arquivo = e.target.files?.[0];
    if (!arquivo) return;
    setEnviandoLogo(true);
    try {
      const formulario = new FormData();
      formulario.append('logo', arquivo);
      const r = await api.upload('/configuracoes/aparencia/logo', formulario);
      setLogoUrl(r.data.logoUrl);
      await recarregarConfig();
      toast.sucesso('Logotipo atualizado.');
    } catch (err) {
      toast.erro(err.message || 'Não foi possível enviar o logotipo');
    } finally {
      setEnviandoLogo(false);
      if (inputLogoRef.current) inputLogoRef.current.value = '';
    }
  }

  if (carregando) return <Spinner animation="border" />;

  return (
    <>
      <h1 className="h4 mb-3">Configurações do sistema</h1>
      <Form onSubmit={salvar} style={{ maxWidth: 720 }}>
        <Card className="mb-3">
          <Card.Header>Aparência do painel</Card.Header>
          <Card.Body>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Cor principal (menu, cabeçalhos, abas)</Form.Label>
                  <div className="d-flex align-items-center gap-2">
                    <Form.Control
                      type="color" title="Cor principal" style={{ width: 48, padding: 2 }}
                      value={corPrimaria}
                      onChange={(e) => { setCorPrimaria(e.target.value); previsualizarCor('--cor-primaria', e.target.value); }}
                    />
                    <Form.Control
                      value={corPrimaria} autoComplete="off" spellCheck={false}
                      onChange={(e) => { setCorPrimaria(e.target.value); previsualizarCor('--cor-primaria', e.target.value); }}
                    />
                  </div>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Cor de destaque (botão Salvar)</Form.Label>
                  <div className="d-flex align-items-center gap-2">
                    <Form.Control
                      type="color" title="Cor de destaque" style={{ width: 48, padding: 2 }}
                      value={corDestaque}
                      onChange={(e) => { setCorDestaque(e.target.value); previsualizarCor('--cor-destaque', e.target.value); }}
                    />
                    <Form.Control
                      value={corDestaque} autoComplete="off" spellCheck={false}
                      onChange={(e) => { setCorDestaque(e.target.value); previsualizarCor('--cor-destaque', e.target.value); }}
                    />
                  </div>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Button variant="link" className="p-0" onClick={restaurarCores}>Restaurar cores padrão</Button>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Logotipo</Form.Label>
                  <div className="d-flex align-items-center gap-3 flex-wrap">
                    {logoUrl && (
                      <img src={logoUrl} alt="Logotipo atual" style={{ maxWidth: 180, maxHeight: 60 }} />
                    )}
                    <Form.Control
                      ref={inputLogoRef} type="file" accept="image/png,image/jpeg,image/webp"
                      onChange={enviarLogo} disabled={enviandoLogo} style={{ maxWidth: 320 }}
                    />
                    {enviandoLogo && <Spinner animation="border" size="sm" />}
                  </div>
                  <Form.Text className="text-muted">
                    PNG, JPEG ou WEBP, até 2 MB. Aparece no menu, no login e nas telas públicas. Não muda o logo
                    usado nos e-mails nem nas páginas do wl.eimovel.com.br (têm modelo próprio).
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="mb-3">
          <Card.Header>Mapa do cadastro de imóveis</Card.Header>
          <Card.Body>
            <Form.Check
              type="radio" id="mapa-osm" name="mapa" className="mb-2"
              label={<><strong>OpenStreetMap</strong> — gratuito, sem chave (ativo por padrão)</>}
              checked={tipo === 'openstreetmap'} onChange={() => setTipo('openstreetmap')}
            />
            <Form.Check
              type="radio" id="mapa-google" name="mapa"
              label={<><strong>Google Maps</strong> — precisa de chave; uso cobrado pelo Google acima do crédito mensal</>}
              checked={tipo === 'google'} onChange={() => setTipo('google')}
            />
            <Form.Group className="mt-3">
              <Form.Label>Chave da API do Google Maps</Form.Label>
              <Form.Control value={chave} onChange={(e) => setChave(e.target.value)} placeholder="AIza…" autoComplete="off" spellCheck={false} />
              <Form.Text className="text-muted">
                Crie uma chave <strong>nova</strong> no Google Cloud (não reaproveite a do Laravel antigo) e restrinja por
                <strong> HTTP referrer</strong> a <code>https://painel.eimovel.com.br/*</code> e pelas APIs
                <strong> Maps JavaScript API</strong> e <strong>Geocoding API</strong>; ligue um teto de orçamento. A chave fica
                visível no navegador — é a restrição no Google que a protege. Se o Google recusar a chave, o painel volta sozinho
                para o OpenStreetMap.
              </Form.Text>
            </Form.Group>
            {tipo === 'google' && !chave.trim() && <Alert variant="warning" className="mt-3 mb-0">Informe a chave para usar o Google Maps.</Alert>}
          </Card.Body>
        </Card>
        {acesso && (
          <Card className="mb-3">
            <Card.Header>Acesso e limites</Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col md={4}>
                  <Form.Group controlId="acesso-senhaMinima">
                    <Form.Label>Tamanho mínimo da senha</Form.Label>
                    <Form.Control type="number" min={4} max={20} step={1} value={acesso.senhaMinima} required
                      onChange={(e) => setAcesso((x) => ({ ...x, senhaMinima: e.target.value }))} />
                    <Form.Text className="text-muted">Vale para trocar, redefinir e definir senhas. Padrão: 6.</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId="acesso-vinculoTentativasPorHora">
                    <Form.Label>Tentativas erradas de vínculo por hora</Form.Label>
                    <Form.Control type="number" min={1} max={50} step={1} value={acesso.vinculoTentativasPorHora} required
                      onChange={(e) => setAcesso((x) => ({ ...x, vinculoTentativasPorHora: e.target.value }))} />
                    <Form.Text className="text-muted">Código de vínculo errado no WhatsApp (por remetente). Padrão: 5.</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId="acesso-vinculoCodigosPorHora">
                    <Form.Label>Códigos de vínculo por usuário por hora</Form.Label>
                    <Form.Control type="number" min={1} max={100} step={1} value={acesso.vinculoCodigosPorHora} required
                      onChange={(e) => setAcesso((x) => ({ ...x, vinculoCodigosPorHora: e.target.value }))} />
                    <Form.Text className="text-muted">Quantos códigos/links cada usuário pode gerar por hora. Padrão: 10.</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId="acesso-vinculoValidadeMinutos">
                    <Form.Label>Validade do código/link de vínculo (min)</Form.Label>
                    <Form.Control type="number" min={1} max={120} step={1} value={acesso.vinculoValidadeMinutos} required
                      onChange={(e) => setAcesso((x) => ({ ...x, vinculoValidadeMinutos: e.target.value }))} />
                    <Form.Text className="text-muted">Tempo para usar o código ou link depois de gerado. Padrão: 10.</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId="acesso-reconfirmacaoMeses">
                    <Form.Label>Reconfirmar vínculo com o bot (meses)</Form.Label>
                    <Form.Control type="number" min={1} max={36} step={1} value={acesso.reconfirmacaoMeses} required
                      onChange={(e) => setAcesso((x) => ({ ...x, reconfirmacaoMeses: e.target.value }))} />
                    <Form.Text className="text-muted">Depois desse tempo o corretor confirma "ainda sou eu" para abrir um cadastro novo. Padrão: 6.</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId="acesso-ajudaBotHoras">
                    <Form.Label>Instruções automáticas do bot (horas)</Form.Label>
                    <Form.Control type="number" min={1} max={168} step={1} value={acesso.ajudaBotHoras} required
                      onChange={(e) => setAcesso((x) => ({ ...x, ajudaBotHoras: e.target.value }))} />
                    <Form.Text className="text-muted">Intervalo mínimo entre as instruções que o bot manda quando o corretor escreve algo solto. Padrão: 12.</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId="acesso-logoTamanhoMaxMb">
                    <Form.Label>Tamanho máximo do logo (MB)</Form.Label>
                    <Form.Control type="number" min={1} max={10} step={1} value={acesso.logoTamanhoMaxMb} required
                      onChange={(e) => setAcesso((x) => ({ ...x, logoTamanhoMaxMb: e.target.value }))} />
                    <Form.Text className="text-muted">Logo da empresa e logotipo do painel. Padrão: 2.</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId="acesso-fotoTamanhoMaxMb">
                    <Form.Label>Tamanho máximo de cada foto de imóvel (MB)</Form.Label>
                    <Form.Control type="number" min={1} max={50} step={1} value={acesso.fotoTamanhoMaxMb} required
                      onChange={(e) => setAcesso((x) => ({ ...x, fotoTamanhoMaxMb: e.target.value }))} />
                    <Form.Text className="text-muted">Padrão: 15.</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId="acesso-senhaPadrao">
                    <Form.Label>Senha inicial padrão</Form.Label>
                    <CampoSenha value={acesso.senhaPadrao} required minLength={6} maxLength={40} autoComplete="off"
                      onChange={(e) => setAcesso((x) => ({ ...x, senhaPadrao: e.target.value.replace(/\s/g, '') }))} />
                    <Form.Text className="text-muted">
                      Senha de quem acabou de ser criado ou teve a senha redefinida (6 a 40 caracteres, sem espaços). O usuário é obrigado a
                      trocá-la no primeiro acesso. Padrão: 123mudar.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}
        {regras && (
          <Card className="mb-3">
            <Card.Header>Regras do cadastro de imóveis</Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col md={4}>
                  <Form.Group controlId="regra-janela-tipo">
                    <Form.Label>Prazo para trocar o tipo (horas)</Form.Label>
                    <Form.Control type="number" min={1} max={8760} step={1} value={regras.janelaTrocaTipoHoras} required
                      onChange={(e) => setRegras((x) => ({ ...x, janelaTrocaTipoHoras: e.target.value }))} />
                    <Form.Text className="text-muted">
                      Depois desse tempo, o tipo de um imóvel já publicado não pode mais ser trocado (o código já está em portais,
                      mensagens e leads); o caminho é "Copiar imóvel". Rascunhos trocam sempre. Padrão: 12.
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId="regra-raio-busca">
                    <Form.Label>Raio da busca de endereço nos bots (km)</Form.Label>
                    <Form.Control type="number" min={5} max={50} step={1} value={regras.buscaEnderecoRaioKm} required
                      onChange={(e) => setRegras((x) => ({ ...x, buscaEnderecoRaioKm: e.target.value }))} />
                    <Form.Text className="text-muted">Até onde procurar a mesma rua em cidades vizinhas da imobiliária (5 a 50 km). Padrão: 50.</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId="regra-max-opcoes">
                    <Form.Label>Opções de endereço na lista dos bots</Form.Label>
                    <Form.Control type="number" min={2} max={8} step={1} value={regras.buscaEnderecoMaxOpcoes} required
                      onChange={(e) => setRegras((x) => ({ ...x, buscaEnderecoMaxOpcoes: e.target.value }))} />
                    <Form.Text className="text-muted">Quantas opções o bot mostra para o corretor escolher (2 a 8). Padrão: 5.</Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}
        <Card className="mb-3">
          <Card.Header>Cadastro de imóvel pelos bots (WhatsApp e Telegram)</Card.Header>
          <Card.Body>
            <Form.Group controlId="config-timeout-cadastro" style={{ maxWidth: 260 }}>
              <Form.Label>Tempo para concluir o cadastro (minutos)</Form.Label>
              <Form.Control
                type="number" min={5} max={720} step={1} value={timeoutCadastro}
                onChange={(e) => setTimeoutCadastro(e.target.value)} required
              />
              <Form.Text className="text-muted">
                Depois de criar o rascunho, o corretor tem esse tempo (entre 5 e 720 minutos) para mandar fotos e detalhes.
                Se ficar parado, o cadastro é encerrado: na próxima mensagem o bot avisa, gera o título e manda o link do
                painel. O rascunho nunca se perde.
              </Form.Text>
            </Form.Group>
          </Card.Body>
        </Card>
        <Card className="mb-3">
          <Card.Header>Bot do WhatsApp</Card.Header>
          <Card.Body>
            <Form.Group controlId="config-numero-bot">
              <Form.Label>Número do WhatsApp do bot</Form.Label>
              <Form.Control
                type="tel" inputMode="tel" value={numeroBot} onChange={(e) => setNumeroBot(e.target.value)}
                placeholder="(19) 99999-8888" autoComplete="off"
              />
              <Form.Text className="text-muted">
                É o número do WhatsApp que está logado no app WhatsLight (o mesmo que envia as mensagens dos anúncios). Os
                corretores recebem um botão que abre a conversa com ele para conectar o WhatsApp no Perfil. Se o número mudar,
                é só atualizar aqui. Deixe vazio para mostrar apenas o comando, sem o botão.
              </Form.Text>
            </Form.Group>
          </Card.Body>
        </Card>
        <Button type="submit" className="botao-salvar" disabled={salvando}>{salvando ? 'Salvando…' : 'Salvar'}</Button>
      </Form>
    </>
  );
}
